/**
 * Runs work that must not compete with the first paint - third-party tag
 * scripts - once the page has loaded and the main thread is idle, or on the
 * visitor's first interaction, whichever comes first (QA 2026-09-25 #14 /
 * #37: gtag alone was 181 KB on the booking portal's first step).
 *
 * Only the network fetch is deferred. The callers set up each vendor's queue
 * (dataLayer, fbq, uetq, ndp, lintrk) synchronously, so a page view or
 * conversion recorded before the script arrives is queued and sent when it
 * does - nothing is dropped, it is only sent a moment later. A visitor who
 * interacts (tap, key, scroll) triggers the load at once, and so does the
 * page being hidden, so what was queued gets its best chance to go out.
 */

type Task = () => void;

const IDLE_TIMEOUT_MS = 3000;
/** Browsers without requestIdleCallback (Safari): this long after `load`. */
const FALLBACK_DELAY_MS = 1000;
const WAKE_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll'];

// Unit tests load tags synchronously unless a test asks for the real thing.
let immediate = process.env.NODE_ENV === 'test';
let queue: Task[] = [];
let armed = false;
let flushed = false;

function runAll(): void {
  flushed = true;
  const tasks = queue;
  queue = [];
  tasks.forEach((task) => {
    try {
      task();
    } catch (e) {
      // one vendor's failure is not another's
    }
  });
}

function arm(): void {
  if (armed) return;
  armed = true;
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
  };
  let done = false;
  let onHidden: () => void = () => undefined;
  const go = () => {
    if (done) return;
    done = true;
    WAKE_EVENTS.forEach((e) => w.removeEventListener(e, go, true));
    document.removeEventListener('visibilitychange', onHidden);
    runAll();
  };
  onHidden = () => {
    if (document.visibilityState === 'hidden') go();
  };
  WAKE_EVENTS.forEach((e) => w.addEventListener(e, go, {
    capture: true,
    passive: true,
  }));
  document.addEventListener('visibilitychange', onHidden);
  const idle = () => {
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(go, { timeout: IDLE_TIMEOUT_MS });
    } else {
      window.setTimeout(go, FALLBACK_DELAY_MS);
    }
  };
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
}

/** Run `task` after load + idle or on first interaction (see above). */
export function whenIdle(task: Task): void {
  if (immediate || typeof window === 'undefined') {
    task();
    return;
  }
  if (flushed) {
    // The page is past its first paint already: nothing left to protect.
    task();
    return;
  }
  queue.push(task);
  arm();
}

/** Tests only: switch the deferral on (or off) and forget earlier state. */
export function setIdleLoadForTests(defer: boolean): void {
  immediate = !defer;
  queue = [];
  armed = false;
  flushed = false;
}
